import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Image from 'next/image';
import cn from 'classnames';
import gsap from 'gsap';
import { useKeenSlider } from 'keen-slider/react';
import 'keen-slider/keen-slider.min.css';

import Spinner from 'react-scale/components/elements/spinner';

import styles from './slider.module.css';

const DataSectionSlider = ({ images, bleeds, lessPadding }) => {
  const [mounted, setMounted] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const [iframeActive, setIframeActive] = useState(false);
  const [showIframe, setShowIframe] = useState(false);

  useEffect(() => {
    if (showIframe && !iframeActive) {
      const timer = setTimeout(() => {
        setIframeActive(true);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [showIframe, iframeActive]);

  const [sliderRef, slider] = useKeenSlider({
    created() {
      setLoaded(true);
    },
    initial: 0,
    centered: true,
    slides: {
      perView: 1,
      spacing: bleeds ? 24 : 0
    },
    loop: true,
    slideChanged(s) {
      setCurrentSlide(s.track.details.rel);
    },
    dragStarted: () => {
      gsap.to(slider.current.container.querySelectorAll('.keen-slider__slide > div'), {
        scale: 0.9,
        stagger: {
          each: 0.02,
          from: 'start'
        }
      });
      slider.current.container.style.cursor = 'grabbing';
    },
    dragEnded: () => {
      gsap.to(slider.current.container.querySelectorAll('.keen-slider__slide > div'), {
        scale: 1,
        stagger: {
          each: 0.02,
          from: 'start'
        }
      });
      slider.current.container.style.cursor = 'grab';
    }
  });

  const currentImage = images[currentSlide];

  useEffect(() => {
    if (slider && !mounted) {
      slider.current.update();
      setMounted(true);
    }
  }, [slider, mounted]);

  return (
    <div className="relative w-full">
      <div className="relative overflow-hidden rounded-3xl">
        <div className="absolute inset-0 w-full h-full bg-gradient-diagonal" />
        <div>
          {images && (
            <div ref={sliderRef} className="keen-slider">
              {images.map(img => (
                <div
                  className={cn('p-10 sm:p-20 relative text-center keen-slider__slide', {
                    'xl:pr-0 xl:pl-14 xl:-right-6': bleeds,
                    'sm:p-10': lessPadding
                  })}
                  style={{ cursor: 'grab' }}
                  key={img?.epigraph}
                >
                  <div className="flex overflow-hidden shadow-lg rounded-3xl">
                    {img.isIframe ? (
                      <div
                        className={styles.aspectRatio}
                        style={{ '--ratio': bleeds ? 632 / 600 : 1 }}
                      >
                        {showIframe && (
                          <iframe
                            src={img?.src ?? '/'}
                            width="100%"
                            height="100%"
                            className="rounded-3xl"
                            loading="lazy"
                            title={img?.epigraph}
                            style={{ display: iframeActive ? undefined : 'none' }}
                          />
                        )}
                        {!iframeActive && (
                          <div
                            onClick={() => setShowIframe(true)}
                            onMouseEnter={() => setShowIframe(true)}
                          >
                            <Image
                              src={img.placeholderSrc}
                              layout="fill"
                              alt={img?.epigraph}
                              quality={100}
                              priority
                            />
                            {showIframe && <Spinner />}
                          </div>
                        )}
                      </div>
                    ) : (
                      <Image
                        src={img?.src ?? '/'}
                        width={img?.width ?? (bleeds ? 680 : 600)}
                        height={img?.height ?? (bleeds ? 544 : 600)}
                        alt={img?.epigraph}
                        quality={100}
                        priority
                      />
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      {loaded && images && slider && (
        <div className={cn('max-w-3xl mx-auto flex justify-center mt-4')}>
          {images.length > 1 && (
            <span
              className="mr-4 transition-opacity cursor-pointer select-none hover:opacity-50 font-unicode"
              onClick={e => e.stopPropagation() || slider.current.prev()}
            >
              ←
            </span>
          )}
          <p className="w-full text-base text-center sm:w-7/12">{currentImage?.epigraph}</p>
          {images.length > 1 && (
            <span
              className="ml-4 transition-opacity cursor-pointer select-none hover:opacity-50 font-unicode"
              onClick={e => e.stopPropagation() || slider.current.next()}
            >
              →
            </span>
          )}
        </div>
      )}
    </div>
  );
};

DataSectionSlider.propTypes = {
  images: PropTypes.objectOf(PropTypes.shape),
  bleeds: PropTypes.bool,
  lessPadding: PropTypes.bool
};

DataSectionSlider.defaultProps = {
  images: [],
  bleeds: false,
  lessPadding: false
};

export default DataSectionSlider;
